import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getPublicCategories } from '../services/publicService';

const FooterContainer = styled.footer`
  background-color: #000;
  color: #fff;
  padding: 40px 0 20px;
  width: 100%;
  margin-top: 0;
`;

const FooterContent = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  
  @media (min-width: 768px) {
    padding: 0 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 24px;
  }
  
  @media (min-width: 1536px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Column = styled.div`
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex: 0 0 100%;
    margin-bottom: 40px;
  }
`;

const LogoColumn = styled(Column)`
  flex: 0 0 100%;
  max-width: 250px;
  
  @media (min-width: 768px) {
    flex: 0 0 25%;
  }
`;

const ShopColumn = styled(Column)`
  @media (min-width: 768px) {
    flex: 0 0 20%;
  }
`;

const CompanyColumn = styled(Column)`
  @media (min-width: 768px) {
    flex: 0 0 20%;
  }
`;

const SocialColumn = styled(Column)`
  @media (min-width: 768px) {
    flex: 0 0 25%;
  }
`;

const BrandName = styled.h2`
  font-family: 'Archivo Black', sans-serif;
  font-size: 28px;
  font-weight: 900;
  color: #fff;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
  
  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 14px;
  color: #fff;
  line-height: 1.6;
  
  svg {
    margin-right: 10px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const AddressText = styled.div`
  flex: 1;
`;

const ColumnTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #fff;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin-bottom: 10px;
`;

const FooterLink = styled(Link)`
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ExternalLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #fff;
  border-radius: 50%;
  transition: opacity 0.2s;
  
  svg {
    width: 16px;
    height: 16px;
    fill: #000;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const Copyright = styled.div`
  width: 100%;
  margin-top: 40px;
  font-size: 12px;
  color: #888;
`;

const Footer = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getPublicCategories();
        if (response && response.data) {
          // Get parent categories only (those without parent field)
          const parentCategories = response.data.filter(cat => !cat.parent);
          setCategories(parentCategories);
        }
      } catch (err) {
        // Keep default empty categories if fetch fails
      }
    };

    fetchCategories();
  }, []);

  const renderCategoryColumns = () => {
    // Return null if no categories loaded
    if (categories.length === 0) {
      return null;
    }

    // Separate parent and child categories
    const parentCategories = categories.filter(cat => !cat.parent);
    const childCategories = categories.filter(cat => cat.parent).flatMap(cat => cat.children || []);

    // Remove duplicates from child categories
    const uniqueChildCategories = childCategories.filter((child, index, self) =>
      index === self.findIndex(c => c._id === child._id)
    );

    return (
      <>
        <ShopColumn>
          <ColumnTitle>SHOP</ColumnTitle>
          <LinkList>
            {parentCategories.map((category) => (
              <LinkItem key={category._id}>
                <FooterLink to={`/shop/${category.slug}`}>
                  {category.name}
                </FooterLink>
              </LinkItem>
            ))}
          </LinkList>
        </ShopColumn>

        {uniqueChildCategories.length > 0 && (
          <ShopColumn>
            <ColumnTitle>CATEGORIES</ColumnTitle>
            <LinkList>
              {uniqueChildCategories.map((category) => (
                <LinkItem key={category._id}>
                  <FooterLink to={`/shop/${category.slug}`}>
                    {category.name}
                  </FooterLink>
                </LinkItem>
              ))}
            </LinkList>
          </ShopColumn>
        )}
      </>
    );
  };

  return (
    <FooterContainer>
      <FooterContent>
        <LogoColumn>
          <BrandName>TheMadZone</BrandName>
          <ContactInfo>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <AddressText>
              Apartment 2, 45-47 Savile Court<br />
              Hull, England, HU1 3BT
            </AddressText>
          </ContactInfo>
          <ContactInfo>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <ExternalLink href="mailto:hello@themadzone.com">hello@themadzone.com</ExternalLink>
          </ContactInfo>
        </LogoColumn>

        {renderCategoryColumns()}

        <CompanyColumn>
          <ColumnTitle>COMPANY</ColumnTitle>
          <LinkList>
            <LinkItem><FooterLink to="/about-us">About Us</FooterLink></LinkItem>
            <LinkItem><FooterLink to="/gift-cards">Gift Cards</FooterLink></LinkItem>
            <LinkItem><FooterLink to="/support">Support/Help</FooterLink></LinkItem>
            <LinkItem><FooterLink to="/terms-conditions">Terms & Conditions</FooterLink></LinkItem>
            <LinkItem><FooterLink to="/privacy-policy">Privacy Policy</FooterLink></LinkItem>
            <LinkItem><FooterLink to="/return-policy">Return Policy</FooterLink></LinkItem>
            <LinkItem><FooterLink to="/shipping-policy">Shipping Policy</FooterLink></LinkItem>
            <LinkItem><FooterLink to="/faq">FAQs</FooterLink></LinkItem>
          </LinkList>
        </CompanyColumn>

        <SocialColumn>
          <ColumnTitle>FOLLOW US</ColumnTitle>
          <SocialLinks>
            <SocialIcon href="https://www.instagram.com/themad_zone" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
              </svg>
            </SocialIcon>
            <SocialIcon href="mailto:hello@themadzone.com" aria-label="Email">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </SocialIcon>
          </SocialLinks>
        </SocialColumn>

        <Copyright>
          @{new Date().getFullYear()} TheMadZone. All rights reserved<br />
          THEMADZONE LTD - Company number 16470350<br />
          <span style={{ fontSize: '10px', marginTop: '8px', display: 'block' }}>
            Site by <a href="https://www.geeksdesigns.com" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'underline' }}>GeeksDesigns</a>
          </span>
        </Copyright>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 